import { useState, useEffect } from "react";
import { Loader2, LayoutTemplate, BookmarkPlus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";
import { money } from "@/lib/landingSim";
import { errMsg } from "@/components/webadvisor/LeadDetails";

export const TemplatePicker = ({ leadId, planId, isAdmin, onApplied }) => {
  const [list, setList] = useState([]);
  const [tid, setTid] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState("");
  const load = () => api.get("/api/webadvisor/templates").then(r => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const apply = async () => {
    setBusy("apply");
    try { const { data } = await api.post(`/api/webadvisor/leads/${leadId}/apply-template/${tid}`); toast.success(data.message); window.dispatchEvent(new Event("wa-quote-changed")); onApplied(); }
    catch (e) { toast.error(errMsg(e, "Couldn't apply the template")); } finally { setBusy(""); }
  };
  const saveAs = async () => {
    setBusy("save");
    try { await api.post(`/api/webadvisor/templates/from-plan/${planId}`, { name }); toast.success(`Saved “${name}” as a template`); setName(""); load(); }
    catch (e) { toast.error(errMsg(e, "Couldn't save the template")); } finally { setBusy(""); }
  };

  return (
    <div className="bg-blue-50/60 border border-blue-100 rounded-lg p-3 space-y-2" data-testid="template-picker">
      <div className="flex flex-wrap items-center gap-2">
        <LayoutTemplate className="w-4 h-4 text-blue-600" />
        <select value={tid} onChange={e => setTid(e.target.value)} className="flex-1 min-w-[180px] h-9 rounded-md border border-slate-300 bg-white px-2 text-sm" data-testid="template-select">
          <option value="">{list.length ? "Apply a template…" : "No templates yet"}</option>
          {list.map(t => <option key={t.id} value={t.id}>{t.name} — {money(t.estimated_total)}</option>)}
        </select>
        <button onClick={apply} disabled={!tid || !!busy} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold px-3 py-2 rounded-lg disabled:opacity-50" data-testid="template-apply-btn">
          {busy === "apply" && <Loader2 className="w-4 h-4 animate-spin" />}Apply & write contract
        </button>
      </div>
      {isAdmin && (
        <div className="flex flex-wrap items-center gap-2">
          <Input value={name} onChange={e => setName(e.target.value)} placeholder="Template name" className="flex-1 min-w-[180px] h-9 bg-white" data-testid="template-saveas-name" />
          <button onClick={saveAs} disabled={!name.trim() || !!busy} className="inline-flex items-center gap-2 border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 text-sm font-semibold px-3 py-2 rounded-lg disabled:opacity-50" data-testid="template-saveas-btn">
            {busy === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : <BookmarkPlus className="w-4 h-4" />}Save this setup as template
          </button>
        </div>
      )}
      <p className="text-xs text-slate-500">Applying replaces this proposal's price, landing pages, deposit and terms, then the AI rewrites the contract. You still review before sending.</p>
    </div>
  );
};
